import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { Signatory } from "../../administration/admin-domain.js";
import type { Professional } from "../../domain/identity/professional.js";

export default class AdminSignatoriesPage extends HTMLElement {
  private signatories: Signatory[] = [];
  private professionals: Professional[] = [];
  private searchQuery = "";
  private isModalOpen = false;
  private deleteModal = { isOpen: false, id: "", title: "", message: "", error: "" };

  public connectedCallback(): void {
    this.loadData();
    window.addEventListener("active-client-changed", () => this.loadData());
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      const [sigs, profs] = await Promise.all([
        platform.adminService.listSignatories(dummyContext),
        platform.adminService.listProfessionals(dummyContext)
      ]);
      this.signatories = sigs;
      this.professionals = profs;
      this.render();
    } catch (e) {
      console.error("Failed to load signatories", e);
    }
  }

  private render(): void {
    const profMap = new Map(this.professionals.map(p => [p.id, p]));

    const filtered = this.signatories.filter(s => {
      const prof = profMap.get(s.professionalId);
      const term = this.searchQuery.toLowerCase();
      return !term ||
        (prof && prof.name.toLowerCase().includes(term)) ||
        (prof && prof.email.toLowerCase().includes(term)) ||
        (s.roleDescription && s.roleDescription.toLowerCase().includes(term));
    });

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Signatários & Responsáveis Autorizados</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Configuração de signatários formais habilitados para assinatura de relatórios de integridade.</p>
          </div>
          <button id="btn-new-sig" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
            <span>+ Adicionar Signatário</span>
          </button>
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center;">
          <input type="text" id="input-search" placeholder="Buscar por nome do profissional ou papel..." value="${this.searchQuery}" style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PROFISSIONAL</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">TIPO / REGISTRO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PAPEL DE ASSINATURA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="5" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhum signatário configurado para este cliente.</td>
                </tr>
              ` : filtered.map(s => {
                const prof = profMap.get(s.professionalId);
                return `
                  <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                    <td style="padding: 12px 16px;">
                      <div style="font-weight: 600; color: var(--ia-primary);">${prof ? prof.name : s.professionalId}</div>
                      <div style="color: var(--color-text-secondary); font-size: 0.8125rem;">${prof ? prof.email : ''} • ${prof ? prof.company : ''}</div>
                    </td>
                    <td style="padding: 12px 16px;">
                      <span style="background: ${prof?.professionalType === 'ENGINEER' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(2, 132, 199, 0.1)'}; color: ${prof?.professionalType === 'ENGINEER' ? 'var(--color-primary)' : 'var(--color-ink)'}; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600;">
                        ${prof?.professionalType === 'ENGINEER' ? 'PLH (CREA)' : prof?.professionalType === 'INSPECTOR' ? 'INSPETOR (SNQC/CFT)' : (prof?.professionalType || 'PROFISSIONAL')}
                      </span>
                      <div style="font-family: monospace; font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 2px;">
                        ${prof?.crea ? `CREA: ${prof.crea}` : prof?.cft ? `CFT: ${prof.cft}` : ''}
                      </div>
                    </td>
                    <td style="padding: 12px 16px; color: var(--color-text-muted); font-weight: 500;">
                      ${s.roleDescription || (prof?.professionalType === 'ENGINEER' ? 'Engenheiro / PLH' : 'Inspetor Executante')}
                    </td>
                    <td style="padding: 12px 16px;">
                      <span style="background: ${s.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${s.status === 'ACTIVE' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                        ${s.status === 'ACTIVE' ? 'HABILITADO' : 'INATIVO'}
                      </span>
                    </td>
                    <td style="padding: 12px 16px; text-align: right;">
                      ${s.status === 'ACTIVE' ? `
                        <button class="btn-inactivate" data-id="${s.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Remover Acesso</button>
                      ` : ''}
                      <button class="btn-delete" data-id="${s.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-solid); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Excluir</button>
                    </td>
                  </tr>
                `;
              }).join("")}
            </tbody>
          </table>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.deleteModal.isOpen ? this.renderDeleteModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderModal(): string {
    const activeProfs = this.professionals.filter(p => p.status === 'ACTIVE');
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 480px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">Adicionar Signatário Autorizado</h2>
            <button id="btn-close-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>
          <form id="form-sig" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Selecione o Profissional Habilitado *</label>
              <select id="modal-prof" required style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                <option value="">Selecione...</option>
                ${activeProfs.map(p => `
                  <option value="${p.id}">${p.name} (${p.professionalType === 'ENGINEER' ? 'PLH / ' + (p.crea || 'CREA') : 'Inspetor / ' + (p.snqc || p.cft || 'Reg')})</option>
                `).join("")}
              </select>
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Descrição do Papel / Titulação Formal</label>
              <input type="text" id="modal-role" placeholder="Ex: Engenheiro Mecânico Responsável Técnico - PLH" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#btn-new-sig")?.addEventListener("click", () => {
      this.isModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Inativar Signatário",
          message: "Deseja remover este profissional dos signatários autorizados?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Excluir Signatário",
          message: "Atenção: A exclusão requer autorização administrativa. Deseja prosseguir?",
          error: ""
        };
        this.render();
      });
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#form-sig")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const professionalId = (this.querySelector("#modal-prof") as HTMLSelectElement).value;
      const roleDescription = (this.querySelector("#modal-role") as HTMLInputElement).value;

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        await platform.adminService.createSignatory(dummyContext, {
          professionalId,
          roleDescription
        });
        this.isModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        alert(err.message || "Erro ao adicionar signatário.");
      }
    });

    this.querySelector("#form-delete")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const { id, title } = this.deleteModal;
      const isInactivate = title.includes("Inativar");
      
      const passwordAttempt = (this.querySelector("#modal-delete-password") as HTMLInputElement)?.value || "";
      const reason = (this.querySelector("#modal-delete-reason") as HTMLInputElement)?.value || "";

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId || "",
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (isInactivate) {
          await platform.adminService.inactivateSignatory(dummyContext, id);
          this.deleteModal.isOpen = false;
          await this.loadData();
        } else {
          if ((platform.adminService as any).requestProtectedDeletion) {
             const res = await (platform.adminService as any).requestProtectedDeletion(dummyContext, "SIGNATORY", id, passwordAttempt, reason);
             if (res.success) {
               this.deleteModal.isOpen = false;
               await this.loadData();
             } else {
               this.deleteModal.error = res.message || "Erro na exclusão protegida.";
               this.render();
             }
          } else {
             await (platform.adminService as any).deleteSignatory?.(dummyContext, id);
             this.deleteModal.isOpen = false;
             await this.loadData();
          }
        }
      } catch (e: any) {
        this.deleteModal.error = e.message || "Erro ao executar ação.";
        this.render();
      }
    });

    this.querySelector("#btn-cancel-delete")?.addEventListener("click", () => {
      this.deleteModal.isOpen = false;
      this.render();
    });
  }

  private renderDeleteModal(): string {
    const { title, message, error } = this.deleteModal;
    const isInactivate = title.includes("Inativar");
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 450px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); text-align: left;">
          <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 16px 0; text-align: center;">${title}</h2>
          <p style="color: var(--color-text-secondary); font-size: 0.95rem; margin-bottom: 24px; text-align: center;">${message}</p>
          ${error ? `<div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px;">${error}</div>` : ''}
          
          <form id="form-delete" style="display: flex; flex-direction: column; gap: 16px;">
            ${!isInactivate ? `
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Senha do Administrador *</label>
              <input type="password" id="modal-delete-password" required placeholder="Digite sua senha atual" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Motivo *</label>
              <input type="text" id="modal-delete-reason" required placeholder="Motivo da exclusão" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            ` : ''}
            
            <div style="display: flex; justify-content: center; gap: 12px; margin-top: 8px;">
              <button type="button" id="btn-cancel-delete" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--status-danger-solid); border: none; border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">Confirmar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-admin-signatories-page")) {
  customElements.define("app-admin-signatories-page", AdminSignatoriesPage);
}



